// Requester ticket detail + thread (Story #4613, Stream 8b) — mockup 03.
// Thread of public events, a reply composer with a staging dropzone, and
// close/reopen with close_reason-keyed copy. Requester-facing surfaces NEVER
// expose a linked ticket's reference/subject/requester — cross-ticket visibility
// is admin/hub-only, so nothing here renders duplicate_of / linked refs.

import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, Loader2 } from 'lucide-react'
import {
  CLOSE_REASON_OPTIONS,
  reopenCopyFor,
} from '../../constants/support'
import type { CloseReason, SupportEvent, SupportTicket } from '../../types/support'
import {
  closeTicket,
  getTicket,
  replyToTicket,
  reopenTicket,
} from '../../services/support'
import { useSupportAttachments } from '../../hooks/useSupportAttachments'
import {
  deriveDisplayStatus,
  eventAuthor,
  formatAedt,
  sortedEvents,
} from '../../utils/supportDisplay'
import { DisplayStatusChip, RequestTypeBadge } from './SupportBadges'
import AttachmentChip from './AttachmentChip'
import AttachmentDropzone from './AttachmentDropzone'

interface TicketThreadProps {
  ticketId: string
  requesterUserId: string | undefined
  onBack: () => void
  onChanged?: () => void
}

function AttachStrip({ ticket }: { ticket: SupportTicket }) {
  const atts = ticket.attachments ?? []
  if (atts.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {atts.map((a) => (
        <span
          key={a.attachment_id}
          className="flex h-14 w-24 items-center justify-center rounded-md border border-iflow-900/10 bg-gradient-to-br from-iflow-100 to-iflow-200 px-1 text-center text-[10.5px] text-iflow-800"
          title={a.filename}
        >
          <span className="truncate">{a.filename}</span>
        </span>
      ))}
    </div>
  )
}

function Message({ event, ticket, requesterUserId }: { event: SupportEvent; ticket: SupportTicket; requesterUserId?: string }) {
  const who = eventAuthor(event, requesterUserId)
  if (who === 'system') {
    const label = event.event_type === 'ticket_closed' ? 'You closed this ticket' : 'Ticket reopened'
    return <p className="my-2 text-center text-[11.5px] text-slate-500">{label} · {formatAedt(event.created_at)}</p>
  }
  const text = event.event_type === 'ticket_created' ? event.body?.description : event.body?.message
  const isSupport = who === 'support'
  return (
    <div className={`mb-2.5 rounded-xl border bg-white px-3.5 py-3 ${isSupport ? 'border-iflow-900/10 border-l-4 border-l-iflow-500' : 'border-iflow-900/10'}`}>
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-bold text-iflow-900">{isSupport ? 'InnovateFlow Support' : 'You'}</span>
        <span className="text-[11px] text-slate-500">{formatAedt(event.created_at)}</span>
      </div>
      {text && <p className="mt-1.5 whitespace-pre-wrap text-[13px] text-slate-700">{text}</p>}
      {event.event_type === 'ticket_created' && <AttachStrip ticket={ticket} />}
    </div>
  )
}

export default function TicketThread({ ticketId, requesterUserId, onBack, onChanged }: TicketThreadProps) {
  const [ticket, setTicket] = useState<SupportTicket | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [showClose, setShowClose] = useState(false)
  const [closeReason, setCloseReason] = useState<CloseReason>('resolved')
  const [busy, setBusy] = useState(false)

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true)
      setError(null)
      getTicket(ticketId, signal)
        .then((t) => {
          setTicket(t)
          setLoading(false)
        })
        .catch((e) => {
          if ((e as Error)?.name === 'AbortError') return
          setError(e instanceof Error ? e.message : 'Could not load this ticket')
          setLoading(false)
        })
    },
    [ticketId],
  )

  useEffect(() => {
    const ctrl = new AbortController()
    load(ctrl.signal)
    return () => ctrl.abort()
  }, [load])

  const attach = useSupportAttachments({
    ensureTicketId: useCallback(async () => ticketId, [ticketId]),
    lifetimeLocked: ticket?.attachments_locked,
  })

  const closed = ticket?.status === 'closed'

  async function handleSendReply() {
    if (!reply.trim()) return
    setSending(true)
    setActionError(null)
    try {
      await replyToTicket(ticketId, reply.trim())
      setReply('')
      attach.reset()
      load()
      onChanged?.()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not send your reply')
    } finally {
      setSending(false)
    }
  }

  async function handleClose() {
    setBusy(true)
    setActionError(null)
    try {
      await closeTicket(ticketId, closeReason)
      setShowClose(false)
      load()
      onChanged?.()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not close this ticket')
    } finally {
      setBusy(false)
    }
  }

  async function handleReopen() {
    setBusy(true)
    setActionError(null)
    try {
      await reopenTicket(ticketId)
      load()
      onChanged?.()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Could not reopen this ticket')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Loading ticket…
      </div>
    )
  }
  if (error || !ticket) {
    return (
      <div className="py-8">
        <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-[12.5px] text-iflow-600">
          <ArrowLeft size={14} /> Back to My tickets
        </button>
        <p className="rounded-lg bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{error || 'Ticket not found'}</p>
      </div>
    )
  }

  const events = sortedEvents(ticket.events)
  const status = deriveDisplayStatus(ticket, requesterUserId)

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-1 text-[12.5px] text-iflow-600 hover:underline">
        <ArrowLeft size={14} /> Back to My tickets
      </button>
      <h1 className="mt-1.5 text-2xl font-bold text-iflow-900">
        {ticket.ref} — {ticket.subject}
      </h1>
      <div className="mb-4 mt-1 flex flex-wrap items-center gap-2 text-[13px] text-slate-500">
        <RequestTypeBadge type={ticket.request_type} />
        <DisplayStatusChip status={status} />
        {ticket.priority && <span>Priority: {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}</span>}
        <span>· Raised {formatAedt(ticket.created_at)} AEDT</span>
      </div>

      {events.map((e) => (
        <Message key={e.seq} event={e} ticket={ticket} requesterUserId={requesterUserId} />
      ))}

      {actionError && (
        <p role="alert" className="my-2 rounded-lg bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
          {actionError}
        </p>
      )}

      {/* Reply / reopen card */}
      <div className="mt-3 rounded-xl border border-iflow-900/10 bg-white/80 p-4">
        {closed ? (
          <div>
            <h2 className="text-[15px] font-semibold text-iflow-900">This ticket is closed</h2>
            <p className="mt-1 text-[13px] text-slate-600">{reopenCopyFor(ticket.close_reason)}</p>
            <button
              type="button"
              onClick={handleReopen}
              disabled={busy}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-iflow-500 px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-iflow-600 disabled:opacity-60"
            >
              {busy && <Loader2 size={15} className="animate-spin" />} Reopen ticket
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-[15px] font-semibold text-iflow-900">Reply</h2>
            <textarea
              rows={3}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Write your reply… replying moves this ticket back to In progress"
              className="mt-2 w-full resize-y rounded-md border border-slate-300 bg-white px-2.5 py-2 text-[13px] text-slate-900"
            />
            <div className="mt-2.5">
              <AttachmentDropzone
                onFiles={attach.addFiles}
                fileCount={attach.fileCount}
                hasVideo={attach.hasVideo}
                lifetimeLocked={ticket.attachments_locked}
                compact
              />
              {attach.attachments.map((a) => (
                <AttachmentChip key={a.localId} attachment={a} onRemove={attach.remove} onRetry={attach.retry} />
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleSendReply}
                disabled={sending || attach.uploading || !reply.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-iflow-500 px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-iflow-600 disabled:opacity-60"
              >
                {sending && <Loader2 size={15} className="animate-spin" />} Send reply
              </button>
              <div className="flex-1" />
              {!showClose && (
                <button
                  type="button"
                  onClick={() => setShowClose(true)}
                  className="rounded-lg border border-iflow-200 px-3 py-1.5 text-[12.5px] font-semibold text-iflow-700 hover:bg-iflow-50"
                >
                  Close my ticket
                </button>
              )}
            </div>

            {showClose && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-[12.5px] font-semibold text-iflow-900">Why are you closing this ticket?</p>
                <div className="mt-2 space-y-1.5">
                  {CLOSE_REASON_OPTIONS.map((o) => (
                    <label key={o.value} className="flex items-center gap-2 text-[12.5px] text-slate-700">
                      <input
                        type="radio"
                        name="close_reason"
                        value={o.value}
                        checked={closeReason === o.value}
                        onChange={() => setCloseReason(o.value)}
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={busy}
                    className="inline-flex items-center gap-2 rounded-lg bg-iflow-600 px-3.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-iflow-700 disabled:opacity-60"
                  >
                    {busy && <Loader2 size={14} className="animate-spin" />} Confirm close
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowClose(false)}
                    className="rounded-lg px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:bg-slate-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
